/**
 * Every component a view can use: what it's for, its props, and an example. This is the one description of the
 * component set: the validator, the renderer's checks, COMPONENTS.md, and the agent's JSON Schema all come from it.
 * A new component starts here (see the README).
 */
import { FORMATS } from './format.ts'
import { ICON_NAMES } from './icons.ts'
import { t, type Fields } from './schema.ts'
import type { ViewNode } from './types.ts'

export type ComponentGroup = 'layout' | 'content' | 'data' | 'action'

export interface ComponentDef {
  type: string
  group: ComponentGroup
  /** One line: what it is. */
  summary: string
  /** When to reach for it, and when something else fits better. */
  use: string
  props: Fields
  /** A small, real example (it's validated and rendered in the catalog). */
  example: ViewNode
}

// Props several components share.
const gap = t.enum(['none', 's', 'm', 'l'], 'Space between children: s 8px, m 16px, l 24px.', { default: 'm' })
const format = t.enum(FORMATS, 'How to show the value; see Formats.', { optional: true })
const unit = t.string('A unit after the number: "gold", "s", or "%" for a number that is already a percentage.', {
  max: 16,
  optional: true,
})
const icon = t.enum(ICON_NAMES, 'An icon by Softmax name; see Icons.', { optional: true })
const tone = t.enum(
  ['neutral', 'live', 'you', 'featured'],
  "`live`: happening now (pulses). `you`: the player's own. `featured`: the one to look at (lime). Else `neutral`.",
  { default: 'neutral' }
)

const POINT = t.object(
  { x: t.value('Position on x: a number for line charts, a category for bar charts.'), y: t.number('The value.') },
  'A point.'
)

export const COMPONENTS: readonly ComponentDef[] = [
  // ---- layout ----
  {
    type: 'stack',
    group: 'layout',
    summary: 'Children in a column (or a row), evenly spaced.',
    use: "The default way to put things one after another. A view's body is already a column, so reach for a stack to group things or to lay them `across`.",
    props: {
      direction: t.enum(['down', 'across'], '`down` (a column) or `across` (a row).', { default: 'down' }),
      gap,
      align: t.enum(['start', 'center', 'end', 'stretch'], 'Cross-axis alignment.', { default: 'stretch' }),
      justify: t.enum(
        ['start', 'center', 'end', 'between'],
        'Main-axis distribution; `between` pushes the first and last child to the ends.',
        { default: 'start' }
      ),
      wrap: t.boolean('Wrap a row onto more lines when it runs out of room.', { default: false }),
      children: t.nodes('What it holds.'),
    },
    example: {
      type: 'stack',
      direction: 'across',
      gap: 's',
      children: [
        { type: 'badge', text: 'Live', tone: 'live' },
        { type: 'badge', text: 'v14', tone: 'you' },
        { type: 'badge', text: 'Game of the Week', tone: 'featured' },
      ],
    },
  },
  {
    type: 'grid',
    group: 'layout',
    summary: 'Children in equal columns that wrap.',
    use: 'Rows of metrics, a wall of cards. Without `columns`, it fits as many columns of at least `min` px as there is room for, so it works at any width.',
    props: {
      columns: t.number('A fixed number of columns. Leave it out to fit columns to the width.', {
        min: 1,
        max: 6,
        integer: true,
        optional: true,
      }),
      min: t.number('The narrowest a column gets (px) when fitting columns to the width.', {
        min: 80,
        max: 600,
        default: 200,
      }),
      gap,
      children: t.nodes('What it holds; each child is one cell.'),
    },
    example: {
      type: 'grid',
      min: 140,
      children: [
        { type: 'metric', label: 'Win rate', value: 0.472, format: 'percent' },
        { type: 'metric', label: 'Games', value: 1240 },
        { type: 'metric', label: 'Rank', value: 12 },
      ],
    },
  },
  {
    type: 'frame',
    group: 'layout',
    summary: 'A card: a bordered surface with an optional title, around related content.',
    use: "Group what belongs together (a chart and its note, a set of metrics) so it reads as one thing. Don't frame everything: frames inside frames get heavy; use a `section` or a `stack` inside one.",
    props: {
      title: t.string('A title at the top.', { max: 80, optional: true }),
      description: t.string('A line under the title: what this shows and why it matters.', {
        max: 280,
        optional: true,
      }),
      aside: t.nodes('Small things at the top right, beside the title: badges, a button.', { optional: true }),
      children: t.nodes('What it holds.'),
    },
    example: {
      type: 'frame',
      title: 'This week',
      description: 'Since Monday, all leagues.',
      aside: [{ type: 'badge', text: 'Live', tone: 'live' }],
      children: [{ type: 'text', text: 'Three rounds played, two climbs.' }],
    },
  },
  {
    type: 'section',
    group: 'layout',
    summary: 'A labelled part of a page: a small uppercase label, then its content.',
    use: 'To split a page into named parts without boxing them in. Sections in a row space themselves.',
    props: {
      label: t.string('The label, short: "Standings", "Recent games".', { max: 40 }),
      aside: t.nodes('Small things at the right of the label: a count, a button.', { optional: true }),
      children: t.nodes('What it holds.'),
    },
    example: {
      type: 'section',
      label: 'Recent games',
      children: [{ type: 'text', text: 'No games yet.', tone: 'muted' }],
    },
  },
  {
    type: 'tabs',
    group: 'layout',
    summary: 'Panels behind a row of tabs; one shows at a time.',
    use: "Several views of the same thing (Chart / By opponent / Raw) where only one is needed at once. Not for site navigation: that's `nav`.",
    props: {
      tabs: t.array(
        t.object(
          {
            label: t.string("The tab's name.", { max: 32 }),
            count: t.number('A count beside the label.', { optional: true }),
            body: t.nodes('What the panel shows.'),
          },
          'A tab.'
        ),
        'The tabs, in order; the first is open.',
        { max: 8 }
      ),
    },
    example: {
      type: 'tabs',
      tabs: [
        { label: 'Summary', body: [{ type: 'text', text: 'Winning more on the north side.' }] },
        { label: 'Details', body: [{ type: 'text', text: 'North 58%, south 41% over 200 games.', tone: 'muted' }] },
      ],
    },
  },
  {
    type: 'repeat',
    group: 'layout',
    summary: 'One copy of a template for each item in a list.',
    use: 'Cards or metrics for each item of a bound list. Inside the template, bind to the item by the `as` name ("entry.score"). Each copy is laid out by the parent, so a repeat inside a grid fills the grid. For plain rows of fields, `table` or `list` is simpler.',
    props: {
      items: t.array(t.any('An item.'), 'The list, usually a binding: { "$": "board.entries" }.'),
      as: t.string('The name the template uses for each item.', { max: 24, default: 'item' }),
      limit: t.number('Show at most this many.', { min: 1, max: 200, integer: true, optional: true }),
      template: t.node('What to show for each item.'),
    },
    example: {
      type: 'repeat',
      items: [
        { name: 'north', rate: 0.58 },
        { name: 'south', rate: 0.41 },
      ],
      as: 'side',
      template: { type: 'metric', label: { $: 'side.name' }, value: { $: 'side.rate' }, format: 'percent' },
    },
  },

  // ---- content ----
  {
    type: 'heading',
    group: 'content',
    summary: 'A title for the page (level 1) or a part of it (level 2), with an optional line under it.',
    use: 'Level 1 once, at the top of a view. Level 2 for a part that needs a real title rather than a `section` label.',
    props: {
      text: t.string('The title.', { max: 100 }),
      meta: t.string('A line under it: context, a date, a count.', { max: 200, optional: true }),
      level: t.enum([1, 2], '1 for the page, 2 for a part.', { default: 1 }),
    },
    example: { type: 'heading', text: 'Pudge Wars', meta: 'Your policy against the field, last 7 days' },
  },
  {
    type: 'text',
    group: 'content',
    summary: 'A paragraph of plain text.',
    use: 'A sentence or two. For lists, emphasis, links, or code, use `markdown`.',
    props: {
      text: t.string('What it says.', { max: 2000 }),
      tone: t.enum(['default', 'muted', 'danger'], '`muted` for secondary notes, `danger` for problems.', {
        default: 'default',
      }),
      size: t.enum(['body', 'meta'], '`meta` is the smaller size for notes and captions.', { default: 'body' }),
      mono: t.boolean('Monospace, for ids, versions, and numbers in a line.', { default: false }),
    },
    example: { type: 'text', text: 'Hooks land more often from the north lane.', tone: 'muted' },
  },
  {
    type: 'markdown',
    group: 'content',
    summary: 'Formatted text: GitHub-flavored Markdown (lists, emphasis, links, code, tables).',
    use: 'Explanations and findings, written the way the agent writes in chat. Raw HTML is not rendered.',
    props: { text: t.string('The Markdown.', { max: 20000 }) },
    example: { type: 'markdown', text: '**v14** wins more from the north lane:\n\n- 58% north\n- 41% south' },
  },
  {
    type: 'badge',
    group: 'content',
    summary: 'A small rounded label: a status, a version, a tag.',
    use: 'Short status beside a title or in a row. One or two words.',
    props: { text: t.string('What it says: one or two words.', { max: 32 }), tone, icon },
    example: { type: 'badge', text: 'Champion', tone: 'featured', icon: 'leaderboard' },
  },
  {
    type: 'avatar',
    group: 'content',
    summary: 'A round picture of a player: their image, else their initial.',
    use: "Beside a player's name. With `with_name`, it shows the name too.",
    props: {
      name: t.string("The player's name.", { max: 80 }),
      src: t.string('Their image: a path on this site. Images from elsewhere are refused.', { optional: true }),
      size: t.number('Diameter in px.', { min: 16, max: 96, default: 28 }),
      with_name: t.boolean('Show the name beside it.', { default: false }),
    },
    example: { type: 'avatar', name: 'Mason', with_name: true },
  },

  // ---- data ----
  {
    type: 'metric',
    group: 'data',
    summary: 'One number that matters, big, with its label and how it changed.',
    use: 'The headline numbers: win rate, rank, score. Put a few in a `grid`. Give `delta` and `better` to show the change in green or red; `trend` draws a small line of recent values.',
    props: {
      label: t.string('What the number is: "Win rate".', { max: 40 }),
      value: t.value('The number (or a short text, like a rank "#3").'),
      format,
      unit,
      delta: t.number('The change since the comparison, in the same units as the value (a fraction for `percent`).', {
        optional: true,
      }),
      better: t.enum(['higher', 'lower'], 'Which way is good, for coloring the change.', { default: 'higher' }),
      caption: t.string('A note under it: what the change is against ("vs v13"), or the sample ("200 games").', {
        max: 80,
        optional: true,
      }),
      trend: t.array(t.number('A value.'), 'Recent values, oldest first, drawn as a small line.', {
        max: 100,
        optional: true,
      }),
    },
    example: {
      type: 'metric',
      label: 'Win rate',
      value: 0.472,
      format: 'percent',
      delta: 0.031,
      caption: 'vs v13, 200 games',
      trend: [0.38, 0.41, 0.4, 0.44, 0.472],
    },
  },
  {
    type: 'chart',
    group: 'data',
    summary: 'A line or bar chart with a legend, hover readouts, and a table view of the same data.',
    use: "`line` for something over a numeric x (game time, turn, date as a number); `bar` for a breakdown by category. One series per thing compared. A series is its `points`, or live data `rows` plotted by `x_field` and `y_field`. Series kinds set the colors: `version` for policy versions (one blue ramp, newest darkest), `comparison` for context like the field's leaders (gray, dashed), `group` for other named groups (distinct colors, at most three).",
    props: {
      title: t.string('What it shows: "Gold earned over a game".', { max: 80, optional: true }),
      description: t.string('What to see in it.', { max: 400, optional: true }),
      chart: t.enum(['line', 'bar'], 'The kind of chart.'),
      x: t.object(
        { label: t.string('Axis name.', { max: 40 }), unit: t.string('Axis unit.', { max: 16, default: '' }) },
        'The x axis.'
      ),
      y: t.object(
        {
          label: t.string('Axis name.', { max: 40 }),
          unit: t.string('Axis unit; "%" for percentages.', { max: 16, default: '' }),
        },
        'The y axis.'
      ),
      series: t.array(
        t.object(
          {
            name: t.string('The legend name: "v14", "Top 3".', { max: 40 }),
            kind: t.enum(['version', 'comparison', 'group'], 'What it is; sets its color.', { default: 'group' }),
            version: t.number('For `version` series: the version number (orders the ramp).', {
              integer: true,
              optional: true,
            }),
            n: t.number("How many games (or samples) it's from; shown in the legend.", {
              integer: true,
              optional: true,
            }),
            points: t.array(POINT, 'Its points. Or plot data rows instead: rows, x_field, y_field.', {
              max: 500,
              optional: true,
            }),
            rows: t.array(
              t.record('A row.'),
              'Data rows to plot, usually a binding ({ "$": "board" }); x_field and y_field say which fields.',
              { max: 500, optional: true }
            ),
            x_field: t.string('With rows: the field for x ("player_name", "round.number").', {
              max: 80,
              optional: true,
            }),
            y_field: t.string('With rows: the field for y ("score").', { max: 80, optional: true }),
          },
          'A series.'
        ),
        'The series.',
        { max: 8 }
      ),
    },
    example: {
      type: 'chart',
      title: 'Win rate by spawn side',
      chart: 'bar',
      x: { label: 'Side' },
      y: { label: 'Win rate', unit: '%' },
      series: [
        {
          name: 'v13',
          kind: 'version',
          version: 13,
          n: 180,
          points: [
            { x: 'North', y: 51 },
            { x: 'South', y: 39 },
          ],
        },
        {
          name: 'v14',
          kind: 'version',
          version: 14,
          n: 200,
          points: [
            { x: 'North', y: 58 },
            { x: 'South', y: 41 },
          ],
        },
        {
          name: 'Top 3',
          kind: 'comparison',
          points: [
            { x: 'North', y: 55 },
            { x: 'South', y: 52 },
          ],
        },
      ],
    },
  },
  {
    type: 'table',
    group: 'data',
    summary: 'Rows of records in columns, numbers right-aligned.',
    use: 'Comparing several fields across many rows: standings, matchups, episodes. Bind `rows` to a list from the data; each column reads one field (a dot path into the row).',
    props: {
      columns: t.array(
        t.object(
          {
            key: t.string('The field to read from each row: "score", "player.name".', { max: 80 }),
            label: t.string('The column heading.', { max: 40 }),
            format,
            unit,
            align: t.enum(['start', 'end'], 'Defaults to `end` for numbers, `start` otherwise.', { optional: true }),
          },
          'A column.'
        ),
        'The columns, left to right.',
        { max: 12 }
      ),
      rows: t.array(t.record('A row.'), 'The rows, usually a binding.'),
      limit: t.number('Show at most this many rows.', { min: 1, max: 500, integer: true, optional: true }),
      empty: t.string('What to say when there are no rows.', { max: 120, default: 'Nothing yet.' }),
    },
    example: {
      type: 'table',
      columns: [
        { key: 'rank', label: '#' },
        { key: 'player', label: 'Player' },
        { key: 'score', label: 'Score', format: 'number' },
        { key: 'win_rate', label: 'Win rate', format: 'percent' },
      ],
      rows: [
        { rank: 1, player: 'ember', score: 1840.2, win_rate: 0.64 },
        { rank: 2, player: 'mason', score: 1791.7, win_rate: 0.58 },
        { rank: 3, player: 'quill', score: 1702, win_rate: 0.55 },
      ],
    },
  },
  {
    type: 'list',
    group: 'data',
    summary: 'A list of rows: a name, a detail, and a note at the right; rows can be links.',
    use: 'Things you open or scan by name: policies, episodes, opponents. For many numeric fields, use `table`.',
    props: {
      items: t.array(
        t.object(
          {
            title: t.string('The name.', { max: 120 }),
            detail: t.string('A detail in the middle.', { max: 120, optional: true }),
            meta: t.string('A short note at the right, like a time.', { max: 60, optional: true }),
            badge: t.string('A badge after the name.', { max: 32, optional: true }),
            href: t.string('Where the row goes: a path on this site like "/policies", or an https link.', {
              optional: true,
            }),
          },
          'A row.'
        ),
        'The rows.',
        { max: 200 }
      ),
      empty: t.string('What to say when there are none.', { max: 120, default: 'Nothing yet.' }),
    },
    example: {
      type: 'list',
      items: [
        { title: 'hook-bot', detail: 'v14', meta: '2 hours ago', badge: 'Champion' },
        { title: 'farm-first', detail: 'v3', meta: 'yesterday' },
      ],
    },
  },
  {
    type: 'nav',
    group: 'data',
    summary: 'A row of links, styled as tabs, the current one underlined.',
    use: 'Moving between pages or routes. For switching panels within a view, use `tabs`.',
    props: {
      label: t.string('What the links are, for screen readers: "Policy pages".', { max: 60 }),
      items: t.array(
        t.object(
          {
            label: t.string('The link text.', { max: 32 }),
            href: t.string('Where it goes: a path on this site like "/policies", or an https link.'),
            count: t.number('A count beside it.', { optional: true }),
            current: t.boolean('The page you are on.', { default: false }),
          },
          'A link.'
        ),
        'The links.',
        { max: 12 }
      ),
    },
    example: {
      type: 'nav',
      label: 'Leaderboards',
      items: [
        { label: 'Mine', href: '/leaderboards', current: true, count: 3 },
        { label: 'All', href: '/leaderboards/all' },
      ],
    },
  },

  // ---- action ----
  {
    type: 'button',
    group: 'action',
    summary: 'A button that opens a link or asks the agent something.',
    use: '`primary` (lime) for the one action that matters most on the screen; else `secondary`, or `ghost` for quiet ones. `{ "ask": … }` sends the agent a message as if the player typed it, which is how a view hands work back to the agent.',
    props: {
      label: t.string('What it does, as a verb: "Open replay".', { max: 40 }),
      action: t.action('What happens on click.'),
      variant: t.enum(['primary', 'secondary', 'ghost'], 'How loud it is.', { default: 'secondary' }),
      icon,
    },
    example: {
      type: 'button',
      label: 'Explain this',
      icon: 'cogent',
      action: { ask: 'Why does v14 lose more from the south lane?' },
    },
  },
  {
    type: 'empty',
    group: 'action',
    summary: 'Nothing to show yet, said plainly, with an optional next step.',
    use: 'Where content will be once something happens. Say what will appear and how to get there.',
    props: {
      text: t.string("What's missing and why.", { max: 200 }),
      action: t.object(
        { label: t.string('The next step.', { max: 40 }), action: t.action('What it does.') },
        'A button for the next step.',
        { optional: true }
      ),
    },
    example: {
      type: 'empty',
      text: 'No games yet. Submit a version to start playing.',
      action: { label: 'Ask my agent', action: { ask: 'Help me submit my policy.' } },
    },
  },

  // ---- the last resort ----
  {
    type: 'custom',
    group: 'content',
    summary: "A component the catalog doesn't have, which you build: your own HTML, CSS, and script, run in a sandbox.",
    use: 'Only when nothing else can show it. Build from the catalog first (combine stack, grid, frame, metric, chart, table, list…); use `custom` for the one piece no component or combination can draw (a game board, a heatmap, a bespoke diagram), and keep everything around it catalog components. Say in `reason` what you looked for and why none fits. It gets its data as `props` (bind them like any prop) and reads Softmax with window.softmax.call (read-only), and has no network, storage, or links out. At most three in a view.',
    props: {
      reason: t.string(
        "Why no catalog component fits: which you considered, and what they can't show. At least a sentence.",
        { max: 400 }
      ),
      html: t.string(
        "The component: HTML with its CSS in an inline <style> and its script in an inline <script>. It fills the component's box and gets Softmax's colors and type as CSS variables (--ink, --line, --paper, --lime…). It can't reach the page, the network, or storage, and it can't take clicks or typing: it only shows things.",
        { max: 64 * 1024 }
      ),
      props: t.record('Data for it, as one object. It reads them as window.softmax.props.', { optional: true }),
      height: t.number('Its height in pixels.', { min: 80, max: 800, integer: true, default: 240 }),
    },
    example: {
      type: 'custom',
      reason: 'A hex map of where hooks landed: no component draws a board; chart only plots x/y series.',
      height: 160,
      props: { hits: [3, 7, 2, 9] },
      html: '<svg viewBox="0 0 120 40" width="100%" height="140"><g id="m"></g></svg><script>var h=window.softmax.props.hits,g=document.getElementById("m");h.forEach(function(n,i){var c=document.createElementNS("http://www.w3.org/2000/svg","circle");c.setAttribute("cx",15+i*30);c.setAttribute("cy",20);c.setAttribute("r",4+n);c.setAttribute("fill","var(--blue-800)");g.appendChild(c);});</script>',
    },
  },
]

const BY_TYPE = new Map(COMPONENTS.map((c) => [c.type, c]))

export function component(type: string): ComponentDef | undefined {
  return BY_TYPE.get(type)
}

export const COMPONENT_TYPES = COMPONENTS.map((c) => c.type)
